import { useTranslation } from '@cobliteam/i18n';
import { Button, Dialog, Typography } from '@cobliteam/ui/components';
import type { ReactNode } from 'react';

export interface SetupGuideAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Only meaningful on the primary action (design-system Button spinner). */
  loading?: boolean;
  /** Optional leading icon (e.g. the paper-plane on "Convidar usuários"). */
  startIcon?: ReactNode;
  'data-testid'?: string;
}

export interface DialogSetupGuideShellProps {
  open: boolean;
  /** Fired by the X, Esc, overlay click, and the default Cancel link. */
  onClose: () => void;
  /** Heading of the modal — wired to aria-labelledby via Radix. */
  title: string;
  /** Grey subtitle under the title — wired to aria-describedby via Radix. */
  description?: string;
  /** `lg` = creation modal (900px, default) · `sm` = success/confirm modal (440px). */
  size?: 'sm' | 'lg';
  /** Content slot: the entity's form (or a pre-built success body). */
  children: ReactNode;
  /** Left-aligned "Cancelar" link. Defaults to the shared translation. */
  cancelLabel?: string;
  /** Defaults to `onClose`. */
  onCancel?: () => void;
  /** Hides the left Cancel link (keeps the footer layout so the primary stays right-aligned). */
  hideCancel?: boolean;
  /** Optional link shown to the right, before the submit button. */
  secondaryAction?: SetupGuideAction;
  /** Primary submit button. */
  primaryAction?: SetupGuideAction;
  /** Hides the footer entirely. */
  hideFooter?: boolean;
  /** Escape hatch: replaces the whole footer. Prefer the declarative props above. */
  footer?: ReactNode;
  /** Extra className for the body slot (e.g. custom form paddings). */
  bodyClassName?: string;
  'data-testid'?: string;
}

/**
 * Shared shell for the Setup Guide entity modals (Header / content slot / Footer),
 * built on the design-system Dialog. Each entity fills the slot with its own form and
 * declares the footer actions; the layout (Cancel left, secondary + submit right) is
 * fixed here so every entity modal stays consistent. See {@link DialogSetupGuideSuccess}
 * for the paired success screen.
 */
export const DialogSetupGuideShell = ({
  open,
  onClose,
  title,
  description,
  size = 'lg',
  children,
  cancelLabel,
  onCancel,
  hideCancel,
  secondaryAction,
  primaryAction,
  hideFooter,
  footer,
  bodyClassName,
  'data-testid': dataTestId,
}: DialogSetupGuideShellProps) => {
  const { t } = useTranslation('ui-business');

  const footerContent = footer ?? (
    <>
      {hideCancel ? (
        // Keep the left slot so justify-between still pushes the primary action to the right.
        <span />
      ) : (
        <Button variant='link' color='default' onClick={onCancel ?? onClose}>
          {cancelLabel ?? t('dialogs.setup_guide.cancel')}
        </Button>
      )}
      <div className='flex items-center gap-4'>
        {secondaryAction && (
          <Button variant='link' onClick={secondaryAction.onClick} disabled={secondaryAction.disabled} data-testid={secondaryAction['data-testid']}>
            {secondaryAction.label}
          </Button>
        )}
        {primaryAction && (
          <Button
            variant='contained'
            color='primary'
            loading={primaryAction.loading}
            disabled={primaryAction.disabled}
            startIcon={primaryAction.startIcon}
            onClick={primaryAction.onClick}
            data-testid={primaryAction['data-testid']}
          >
            {primaryAction.label}
          </Button>
        )}
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* `dialog-lg` is a fixed 900px width; cap it so the modal never overflows narrow viewports. */}
      <Dialog.Content size={size} className='max-w-[calc(100vw-3rem)]' data-testid={dataTestId}>
        {/* items-start keeps the X aligned to the top of the stacked title + description. */}
        <Dialog.Header className='items-start'>
          <div className='flex flex-col gap-1'>
            <Dialog.Title>{title}</Dialog.Title>
            {description ? (
              <Typography variant='text-md' className='text-gray-11' asChild>
                <Dialog.Description>{description}</Dialog.Description>
              </Typography>
            ) : (
              // Radix warns without a description; keep an accessible one when there is no visible subtitle.
              <Dialog.Description className='sr-only'>{title}</Dialog.Description>
            )}
          </div>
          <Dialog.Close />
        </Dialog.Header>
        <Dialog.Body className={bodyClassName}>{children}</Dialog.Body>
        {!hideFooter && <Dialog.Footer className='flex items-center justify-between gap-2'>{footerContent}</Dialog.Footer>}
      </Dialog.Content>
    </Dialog>
  );
};
